import React, { useState } from "react";
import OTPVerificationPage from "./OTPVerificationPage";
import SignupForm from "./SignupForm";
import LoginForm from "./LoginForm";
import axios from "axios";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Ban, BedDouble, CalendarCheck, IndianRupee, ShieldCheck, Video } from "lucide-react";
import Logo from "../../Common/Logo";

const ACCOUNTS_URL = process.env.REACT_APP_ACCOUNTS_URL;
const HOME_URL = process.env.REACT_APP_HOME_URL;

const AuthScreen = ({ setProfileObject }) => {
  const [openOTPPage, setOpenOTPPage] = useState(false);
  const [openSignup, setOpenSignUp] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [isNewSignup, setIsNewSignup] = useState(false);
  // Set by the portals when they sign a user out because their hospital's access was blocked.
  const [searchParams] = useSearchParams();
  const blockedMessage = searchParams.get("blocked");

  const validateInput = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!value) {
      return "Email or Mobile Number is required";
    } else if (phoneRegex.test(value)) {
      return "";
    } else if (emailRegex.test(value)) {
      return "";
    } else if (/[^0-9]+/.test(value)) {
      return "Please enter a valid email address";
    } else {
      return "Please enter a valid 10-digit mobile number";
    }
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    setInputValue(value);
    setError(validateInput(value));
  };

  /** Ask the server to send a sign-in code to this e-mail address or 10-digit mobile number. */
  const sendCode = async (identifier) => {
    const isEmail = !/^[0-9]{10}$/.test(identifier);
    setIsLoading(true);
    try {
      const res = await axios.post(`${ACCOUNTS_URL}/auth/login`, isEmail ? { email: identifier } : { mobile: identifier });
      setEmail(identifier);
      setSentTo(res.data?.data?.sentTo || identifier);
      setOpenOTPPage(true);
      toast.success(`Code sent to ${res.data?.data?.sentTo || identifier}`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.error?.message || "We couldn't send the code. Please try again.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    const currentError = validateInput(inputValue);
    setError(currentError);
    if (currentError) {
      toast.error(currentError);
      return;
    }
    setIsNewSignup(false);
    await sendCode(inputValue);
  };

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <BrandPanel />

      <main className="relative flex min-h-screen flex-col">
        <div className="pointer-events-none absolute inset-0 -z-0 bg-[radial-gradient(ellipse_at_top_right,rgba(47,123,255,.08),transparent_60%)]" />
        <header className="relative flex items-center justify-between px-6 py-5 sm:px-10">
          <a href={HOME_URL} className="lg:hidden" aria-label="Health Frigate home">
            <Logo />
          </a>
          <a href={HOME_URL} className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-ink-900">
            <ArrowLeft className="h-4 w-4" /> Back to website
          </a>
        </header>

        <div className="relative flex flex-1 items-center justify-center px-6 pb-10 sm:px-10">
          <div className="w-full max-w-md">
            {blockedMessage && (
              <div role="alert" className="mb-6 flex gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                <Ban className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
                <div>
                  <p className="font-semibold">Access blocked</p>
                  <p className="mt-1">{blockedMessage}</p>
                  <p className="mt-1 text-rose-700">Please contact Health Frigate support or settle any pending invoices to get access back.</p>
                </div>
              </div>
            )}
            {openOTPPage ? (
              <OTPVerificationPage
                setOpenOTPPage={setOpenOTPPage}
                resend={() => sendCode(email)}
                email={email}
                sentTo={sentTo}
                isNewSignup={isNewSignup}
                setProfileObject={setProfileObject}
              />
            ) : openSignup ? (
              <SignupForm
                setOpenSignUp={setOpenSignUp}
                onRegistered={async (newEmail) => {
                  // New accounts confirm their e-mail with a code before choosing a hospital.
                  setIsNewSignup(true);
                  await sendCode(newEmail);
                }}
              />
            ) : (
              <LoginForm
                setOpenSignUp={setOpenSignUp}
                handleInputChange={(e) => handleInputChange(e)}
                handleSubmit={handleSubmit}
                error={error}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>

        <footer className="relative flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-6 pb-6 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Health Frigate</span>
          <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-ink-900">Terms</a>
          <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-ink-900">Privacy</a>
        </footer>
      </main>
    </div>
  );
};

const PORTALS = [
  { icon: CalendarCheck, title: "Doctors & staff", body: "Day list, check-ins and e-prescriptions" },
  { icon: Video, title: "Patients", body: "Book visits and join video consults" },
  { icon: BedDouble, title: "Hospital admin", body: "Locations, wards, rosters and pricing" },
  { icon: IndianRupee, title: "Billing", body: "OPD & IPD bills with GST and UPI" },
];

/** Left-hand brand panel, matching the dark sections of the marketing site. */
const BrandPanel = () => (
  <aside className="relative hidden overflow-hidden bg-ink-900 lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16">
    <div className="pointer-events-none absolute inset-0">
      <div className="bg-grid absolute inset-0" />
      <div className="absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-brand-600/40 blur-3xl" />
      <div className="absolute -bottom-40 right-0 h-[26rem] w-[26rem] rounded-full bg-care-500/25 blur-3xl" />
    </div>

    <a href={HOME_URL} className="relative" aria-label="Health Frigate home">
      <Logo light />
    </a>

    <div className="relative max-w-lg">
      <p className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-care-300">
        One login · four portals
      </p>
      <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight !text-white xl:text-5xl">
        Welcome back to your{" "}
        <span className="bg-gradient-to-r from-brand-300 to-care-300 bg-clip-text text-transparent">care workspace</span>
      </h1>
      <p className="mt-5 text-lg text-slate-300">
        Sign in once — we'll take you straight to the right portal for your role.
      </p>

      <ul className="mt-10 grid grid-cols-2 gap-3">
        {PORTALS.map((p) => (
          <li key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-care-500 text-white">
              <p.icon className="h-4 w-4" />
            </span>
            <p className="mt-3 text-sm font-semibold text-white">{p.title}</p>
            <p className="mt-0.5 text-xs text-slate-400">{p.body}</p>
          </li>
        ))}
      </ul>
    </div>

    <div className="relative flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
      <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-care-300" /> ABDM ready</span>
      <span>FHIR R4 · SNOMED CT</span>
      <span>Role-based access</span>
    </div>
  </aside>
);

export default AuthScreen;
